<template>
	<footer class="menu-footer">
		<div class="menu-footer__separator">
			<div class="menu-footer__separator__border" />
		</div>
		<div class="menu-footer__content">
			<section id="footerSchedule" class="menu-footer__content__section">
				<Schedule ref="schedule" />
			</section>
			<section id="footerAddress" class="menu-footer__content__section second">
				<Collapse ref="locationCollapse">
					<template #collapse-header>
						<h3 class="menu-footer__content__section__title">Location</h3>
					</template>
					<template #collapse-body>
						<address class="menu-footer__content__section__address">
							<template v-if="address">
								{{ formattedAddress.firstRow }}
								<br />
								{{ formattedAddress.secondRow }}
							</template>
							<template v-else>
								-
							</template>
						</address>
						<template v-if="address">
							<div class="menu-footer__content__section__directions">
								<a :href="mapLink" target="_blank" rel="noopener" class="link">
									Directions
								</a>
							</div>
							<google-map class="menu-footer__content__section__map" />
						</template>
					</template>
				</Collapse>
			</section>
		</div>
		<apps-banner />
	</footer>
</template>

<script>
import { mapState } from 'vuex'
// Components
import Schedule from '@/components/Schedule.vue'
import Collapse from '@/components/Coollapse.vue'
import GoogleMap from '@/components/GoogleMap.vue'
import AppsBanner from '@/components/AppsBanner.vue'
// Utils
import { EventBus } from '@/utils/eventBus'
import { getAddressLink } from '@/utils/helpers'

export default {
	name: 'MenuFooter',

	components: { AppsBanner, GoogleMap, Collapse, Schedule },

	computed: {
		...mapState('venue', ['venueInfo']),

		address() {
			return this.venueInfo.address
		},

		formattedAddress() {
			if (!this.address) return ''

			return this.address.addressLine2
				? {
						firstRow: `${this.address.addressLine1} ${this.address.addressLine2}`,
						secondRow: `${this.address.city}, ${this.address.region} ${this.address.postalCode}`
				  }
				: {
						firstRow: `${this.address.addressLine1}`,
						secondRow: `${this.address.city}, ${this.address.region} ${this.address.postalCode}`
				  }
		},

		mapLink() {
			return getAddressLink(this.address || {})
		}
	},

	created() {
		EventBus.$on('open-footer', id => this.openFooter(id))
	},

	beforeDestroy() {
		EventBus.$off('open-footer')
	},

	methods: {
		openFooter(id) {
			this.$refs.locationCollapse?.open()
			this.$refs.schedule?.$refs?.scheduleCollapse?.open()

			setTimeout(() => this.scrollToFooter(id), 200)
		},

		getHeight(selector) {
			const el = document.querySelector(selector)
			return el ? Math.round(el.getBoundingClientRect().height) : 0
		},

		scrollToFooter(selector) {
			const targetElement = document.querySelector(selector)
			if (!targetElement) {
				EventBus.$emit('footer-scroll-finished')
				return
			}

			const headerHeight = this.getHeight('.header')
			const currentScrollY = window.scrollY || window.pageYOffset || 0

			const targetY =
				targetElement.getBoundingClientRect().top +
				currentScrollY -
				headerHeight
			const finalY = Math.max(0, targetY)

			window.scrollTo({
				top: finalY,
				behavior: 'smooth'
			})

			this.waitForScrollFinish(finalY)
		},

		waitForScrollFinish(targetY) {
			// Not for production: Expo temporary polling to notify Products
			// when footer smooth scroll is finished
			const thresholdPx = 2
			const maxWaitMs = 2000
			const startTime = Date.now()

			const tick = () => {
				const currentY = window.scrollY || window.pageYOffset || 0
				const isReached = Math.abs(currentY - targetY) <= thresholdPx
				const isTimedOut = Date.now() - startTime >= maxWaitMs

				if (isReached || isTimedOut) {
					EventBus.$emit('footer-scroll-finished')
					return
				}

				requestAnimationFrame(tick)
			}

			requestAnimationFrame(tick)
		}
	}
}
</script>

<style scoped lang="scss">
@import './src/assets/styles/variables';

.menu-footer {
	color: $white;

	.banner-bottom {
		position: relative;
		padding: 8px;
		background: $spectre-violet;
		border: 1px solid rgba($white, 0.2);
		border-bottom: none;
		border-top-right-radius: 12px;
		border-top-left-radius: 12px;
		z-index: 1;

		/deep/ .store-button {
			margin-bottom: 0;
			margin-top: 0;
		}

		/deep/ .banner-title {
			font-weight: 700;
		}

		/deep/ .btn-close {
			display: none;
		}
	}

	&__separator {
		padding: 36px 0;

		&__border {
			width: 100%;
			height: 1px;
			background-color: rgba($white, 0.2);
		}
	}

	&__content {
		padding: 24px 0;
		display: flex;

		&__section {
			width: 50%;

			&__title {
				margin: 0 0 24px 0;
				font-size: 16px;
				font-weight: 700;
			}

			&__address,
			&__directions {
				margin-bottom: 8px;
				font-size: 16px;
			}

			&__directions {
				.link {
					margin-bottom: 25px;
					color: $semi-dark-blue;
					text-decoration: none;
				}
			}

			&__map {
				width: 100%;
				height: 436px;
				background-color: grey;
			}

			&.second {
				min-width: 718px;

				@media (max-width: 1142px) {
					min-width: 540px;
				}
			}
		}
	}

	@media (max-width: 1142px) {
		&__separator {
			padding: 36px 0;
		}
	}

	@media (max-width: 940px) {
		&__content {
			flex-direction: column;

			&__section {
				width: 100%;

				&.second {
					min-width: auto;
				}
			}

			/deep/ .schedule-content {
				max-width: none;
				min-height: auto;

				.schedule-list {
					max-width: none;
					margin-bottom: 16px;
				}
			}
		}
	}
}
</style>
